// Pure helper functions for the Processed Payments history screen.
// Kept dependency-free (no redux/react) so they're easy to unit test in isolation.
// Mirrors the conventions established in DailyProcessing/helpers.js.

export { formatReviewReason } from '../../../utils/gatewayResponse'

export const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'cleared', label: 'Cleared' },
  { key: 'pending', label: 'Pending' },
  { key: 'returnedDeclined', label: 'Returned/Declined' },
  { key: 'scheduledRetry', label: 'Scheduled Retry' },
  // Payments PARKed for human review (gateway fault, stranded reference, etc). This is the
  // one-click "parked queue" — without it these rows were previously indistinguishable from
  // healthy in-flight 'pending' payments in the table.
  { key: 'needsReview', label: 'Needs Review' },
  // Reachable since the unified refund/void work (plan item A5): the backend's
  // bulkProcessing.resolveProcessedStatus now derives both from a deliberate reversal
  // ('Voided'/'Cancelled') and from a CONFIRMED full refund. Without these entries a voided or
  // refunded payment matches NO tab except "All" — it vanishes from "Cleared", where operators
  // look for it, and from "Returned/Declined" too.
  { key: 'voided', label: 'Voided' },
  { key: 'refunded', label: 'Refunded' },
]

/**
 * Whether a processed-history row belongs to the given status tab.
 * 'returnedDeclined' is a combined bucket covering both 'returned' and 'declined'.
 */
export const matchesStatusTab = (row, tabKey) => {
  const status = row?.status
  switch (tabKey) {
    case 'cleared':
      return status === 'cleared'
    case 'pending':
      return status === 'pending'
    case 'returnedDeclined':
      return status === 'returned' || status === 'declined'
    case 'scheduledRetry':
      return status === 'scheduledRetry'
    case 'needsReview':
      return status === 'needsReview'
    // A charge an operator deliberately killed, and a charge that cleared and was then fully
    // refunded. Each gets its OWN tab rather than joining an existing bucket: a void is not a
    // collections event (so not Returned/Declined) and a refund is not an outstanding balance.
    case 'voided':
      return status === 'voided'
    case 'refunded':
      return status === 'refunded'
    case 'all':
    default:
      return true
  }
}

/**
 * Client-side search across client name, payment id, and client code.
 * Case-insensitive substring match. Empty/undefined search matches everything.
 */
export const matchesSearch = (row, search) => {
  const term = (search || '').trim().toLowerCase()
  if (!term) return true
  const haystack = [row?.clientName, row?.paymentId, row?.clientCode].filter(Boolean).join(' ').toLowerCase()
  return haystack.includes(term)
}

const matchesProcessor = (row, processor) => {
  if (!processor || processor === 'all') return true
  return row?.processor === processor
}

const matchesPaymentType = (row, paymentType) => {
  if (!paymentType || paymentType === 'all') return true
  return row?.method?.type === paymentType
}

/**
 * Applies the full filter set (status tab, search, processor, payment type)
 * used by the Processed Payments table.
 */
export const filterHistoryRows = (rows = [], { tabKey = 'all', search = '', processor = 'all', paymentType = 'all' } = {}) =>
  rows.filter(
    (row) =>
      matchesStatusTab(row, tabKey) &&
      matchesSearch(row, search) &&
      matchesProcessor(row, processor) &&
      matchesPaymentType(row, paymentType)
  )

/**
 * Row counts per status tab, for the tab labels (e.g. "Cleared (12)").
 * Feed it the rows already filtered by every NON-status facet (search/processor/type)
 * so each tab's number matches what selecting that tab would actually show.
 */
export const getStatusTabCounts = (rows = []) => {
  const counts = {}
  STATUS_TABS.forEach(({ key }) => {
    counts[key] = rows.filter((row) => matchesStatusTab(row, key)).length
  })
  return counts
}

/**
 * KPI aggregates computed CLIENT-side over the currently filtered rows, mirroring the
 * backend's buildKpis shape (count/pct/amount buckets + a bare needsReview count) so
 * KpiCards renders either source identically. The backend KPIs are range-scoped
 * aggregates over the whole response; these follow the applied search/processor/type
 * filters instead, so the cards always describe the rows the operator is looking at.
 * (The placeholder processingFees/netDeposit fields are omitted — their cards are
 * hidden pending a real fee data source.)
 */
export const buildClientKpis = (rows = []) => {
  const totalSubmitted = rows.length
  const pct = (count) => (totalSubmitted ? Math.round((100 * count) / totalSubmitted) : 0)
  const sumAmount = (arr) => arr.reduce((sum, row) => sum + (Number(row?.amount) || 0), 0)
  const bucket = (arr) => ({ count: arr.length, pct: pct(arr.length), amount: sumAmount(arr) })

  return {
    totalSubmitted,
    // 'refunded' counts as CLEARED, mirroring the backend's bulkProcessing.buildKpis exactly. The
    // charge DID clear; a later refund is a separate event, and netting refunds out of revenue is
    // its own approval (plan item A6). THIS SCREEN NEVER USES THE BACKEND KPIs — ProcessedPayments
    // computes its cards from buildClientKpis over the filtered rows — so without this line the
    // visible Cleared card's count and amount would silently drop by every refunded payment the
    // moment the backend started deriving 'refunded'.
    cleared: bucket(rows.filter((row) => row?.status === 'cleared' || row?.status === 'refunded')),
    pending: bucket(rows.filter((row) => row?.status === 'pending')),
    // 'voided' deliberately joins NO bucket — same as the backend. A charge an operator killed on
    // purpose is not a collections event (plan item A5), so it leaves Returned/Declined while
    // still counting toward totalSubmitted.
    returnedDeclined: bucket(rows.filter((row) => row?.status === 'returned' || row?.status === 'declined')),
    needsReview: rows.filter((row) => row?.status === 'needsReview').length,
  }
}

/**
 * Color key ('success' | 'warning' | 'error' | 'info' | 'default') for a row status.
 */
export const getStatusColorKey = (status) => {
  switch (status) {
    case 'cleared':
      return 'success'
    case 'pending':
    // Defensive: the literal 'processing' status value is not currently sent by the
    // backend (in-flight charging payments keep status 'pending' with statusLabel
    // 'Processing'), but map it the same as 'pending' in case it ever is.
    case 'processing':
      return 'warning'
    case 'returned':
    case 'declined':
      return 'error'
    case 'scheduledRetry':
      return 'info'
    // Approved-but-not-yet-charged payments. Distinct from 'pending' (warning) so the
    // two "not settled yet" states are visually distinguishable.
    case 'approvedProcessing':
      return 'info'
    // Parked for human review — blocked on a person and may never settle. Deliberately NOT
    // 'warning' (that's the healthy in-flight 'pending' color) and NOT 'error' (that's a
    // terminal failure; a needsReview row hasn't necessarily failed at all). 'info' is the
    // same "not cleanly resolved, not simply in-flight, not terminal" bucket already used by
    // scheduledRetry/approvedProcessing above.
    case 'needsReview':
      return 'info'
    // A charge that cleared and was then fully refunded. Deliberately NOT 'error' — nothing
    // failed — and NOT 'success', so it is distinguishable at a glance from a payment whose money
    // the business still holds.
    case 'refunded':
      return 'info'
    case 'voided':
    default:
      return 'default'
  }
}

/**
 * Formats a percentage value for display. The backend sends ALL percentages
 * (AI forecasts AND KPI bucket shares) already on a 0-100 scale — e.g.
 * round(100 * cleared / total). Format the value as-is, with NO 0-1 auto-detect:
 * the old `value <= 1 ? value * 100` heuristic inflated legitimate small percents
 * (a 1% share rendered as "100%"). Mirrors DailyProcessing/helpers.js
 * `formatPercent` so both Payments screens agree on the scale.
 */
export const formatPct = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return `${Math.round(value)}%`
}

/**
 * Builds the detail-panel timeline for a processed-history row.
 *
 * Each step's date is now a real lifecycle FACT from row.lifecycle when the
 * backend has it (submittedAt = routedAt, settledAt, returnedAt), falling back
 * to the older approximations for legacy rows that predate those fields. This
 * fills the previously-hardcoded `date: null` Submitted step, and fixes the
 * Returned/Declined/Voided steps, which read settlementDate — a value the
 * backend nulls for exactly those statuses, so they always rendered blank.
 */
export const buildTimeline = (row) => {
  const lc = row?.lifecycle || {}
  const submitted = { label: 'Submitted', date: lc.submittedAt || null, state: 'done' }
  const processed = { label: 'Processed', date: row?.processedOn || null, state: row?.processedOn ? 'done' : 'pending' }

  let final
  switch (row?.status) {
    case 'cleared':
      // Prefer the actual settlement instant (row.settledOn, then the older lifecycle.settledAt);
      // settlementDate is only an estimate.
      final = { label: 'Settled', date: row?.settledOn || lc.settledAt || row?.settlementDate || null, state: 'done' }
      break
    case 'returned':
      // returnedAt is the real return time; settlementDate is null for a returned row.
      final = { label: 'Returned', date: lc.returnedAt || row?.processedOn || null, state: 'done' }
      break
    case 'declined':
      // A first-attempt decline has no returnedAt; processedOn is when it terminally failed.
      final = { label: 'Declined', date: row?.processedOn || null, state: 'done' }
      break
    case 'voided':
      final = { label: 'Voided', date: row?.processedOn || null, state: 'done' }
      break
    // The refund instant comes from row.refund.at (the backend's refund record); processedOn is
    // the ORIGINAL charge date and would misdate the refund step for every row.
    case 'refunded':
      final = { label: 'Refunded', date: row?.refund?.at || row?.processedOn || null, state: 'done' }
      break
    case 'scheduledRetry':
      final = { label: 'Retry Scheduled', date: row?.settlementDate || null, state: 'pending' }
      break
    // Parked for human review. Deliberately its own final step — NOT the 'pending' default
    // below ("Settlement Pending" would wrongly imply this row is still on track to settle,
    // when it's blocked on a person and may never settle at all).
    case 'needsReview':
      final = { label: 'Needs Review', date: row?.settlementDate || null, state: 'pending' }
      break
    case 'pending':
    default:
      final = { label: 'Settlement Pending', date: row?.settlementDate || null, state: 'pending' }
      break
  }

  return [submitted, processed, final]
}

/**
 * Unique processor names present in the rows, sorted alphabetically, for
 * populating the processor filter <Select>.
 */
export const getProcessorOptions = (rows = []) => {
  const set = new Set(rows.map((row) => row?.processor).filter(Boolean))
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

/**
 * Sums the `amount` field across settlement forecast entries, for the
 * footer's total.
 */
export const sumSettlementForecast = (forecast = []) => (forecast || []).reduce((sum, entry) => sum + (Number(entry?.amount) || 0), 0)
